import React, { useState, useContext, useEffect } from "react";
import { Link, useMatch, useNavigate, useParams } from "react-router-dom";
import { GlobalContext } from "../context/Provider";
import henceforthApi from "../utils/henceforthApi";
import Swal from "sweetalert2";
import moment from "moment";
import { Button } from "../components/ui/button";
import {
  Edit,
  Trash2,
  Calendar,
  FileText,
  Loader2,
} from "lucide-react";

interface PolicyDetail {
  created_at: string;
  description: string;
  image_url: string;
  type: string;
  updated_at: string;
  _id: string;
  title: string;
}

const PoliciesDetailsPage: React.FC = () => {
  const match: any = useMatch("/policies/:type");
  const params = useParams();
  const navigate = useNavigate();
  const policyType = match?.params?.type || params.type || "";

  const { authState, handleError } = useContext(GlobalContext);
  if (authState.access_token) {
    henceforthApi.setToken(authState.access_token);
  }

  const [loading, setLoading] = useState(true);
  const [state, setState] = useState<PolicyDetail>({
    created_at: "",
    description: "",
    image_url: "",
    type: policyType,
    updated_at: "",
    _id: "",
    title: "",
  });

  const initialiseData = async () => {
    try {
      setLoading(true);
      const apiRes = await henceforthApi.Policies.byType(policyType);
      const obj =
        Array.isArray(apiRes.data) && apiRes.data.length
          ? apiRes.data[0]
          : apiRes.data;
      if (obj) {
        setState((prev) => ({
          ...prev,
          ...obj,
        }));
      }
    } catch (error: any) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = () => {
    if (!state._id) return;
    Swal.fire({
      title: "Delete Policy?",
      text: "Are you sure you want to delete this policy content?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Yes, delete it",
    }).then(async (result: any) => {
      if (result.isConfirmed) {
        try {
          await henceforthApi.Policies.delete(state._id);
          Swal.fire({
            title: "Policy Deleted",
            text: "Policy has been removed successfully",
            icon: "success",
            confirmButtonColor: "#006747",
            confirmButtonText: "OK",
          });
          navigate("/contents");
        } catch (error: any) {
          Swal.fire({
            title: "Error!",
            text: "Failed to delete policy",
            icon: "error",
            confirmButtonColor: "#006747",
            confirmButtonText: "Close",
          });
        }
      }
    });
  };

  useEffect(() => {
    initialiseData();
  }, [policyType]);

  const formatTitle = (type: string, title?: string) => {
    if (title && title.trim()) return title.trim();
    if (!type) return "Policy Details";
    return type
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const formatDate = (val: any) => {
    if (!val) return null;
    const num = Number(val);
    const m = !isNaN(num) && num > 1000000000 ? moment(num) : moment(val);
    return m.isValid() ? m.format("DD MMM YYYY") : null;
  };

  const updatedDate = formatDate(state.updated_at || state.created_at);
  const displayTitle = formatTitle(state.type || policyType, state.title);

  return (
    <div className="w-full space-y-3 pb-8">
      {/* ── Main Policy Content Card ── */}
      <div className="bg-white border border-slate-200/80 rounded-xl shadow-2xs overflow-hidden">
        {loading ? (
          <div className="flex flex-col items-center justify-center min-h-[360px] text-slate-400 gap-3">
            <Loader2 className="w-8 h-8 animate-spin text-[#006747]" />
            <p className="text-xs font-medium">Loading policy details...</p>
          </div>
        ) : (
          <>
            {/* Header: Title, Last Updated & Actions */}
            <div className="p-4 sm:p-5 border-b border-slate-100 bg-slate-50/50 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                {updatedDate && (
                  <div className="flex items-center gap-1.5 text-xs text-slate-500 font-medium mt-1">
                    <Calendar className="w-3.5 h-3.5 text-slate-400" />
                    <span>Last updated: {updatedDate}</span>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-2 self-start sm:self-center">
                {state._id && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleDelete}
                    className="gap-1.5 h-8 px-2.5 text-xs text-rose-600 hover:text-rose-700 hover:bg-rose-50 border-rose-200"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    <span>Delete</span>
                  </Button>
                )}

                <Link to={`/policies/${policyType}/edit`}>
                  <Button
                    size="sm"
                    className="gap-1.5 h-8 px-3 text-xs shadow-xs"
                  >
                    <Edit className="w-3.5 h-3.5" />
                    <span>Edit Content</span>
                  </Button>
                </Link>
              </div>
            </div>

            {/* Optional Banner Image */}
            {state.image_url && state.image_url !== "null" && (
              <div className="px-4 sm:px-6 pt-4">
                <img
                  src={
                    state.image_url.startsWith("http")
                      ? state.image_url
                      : `${henceforthApi.API_FILE_ROOT_ORIGINAL}${state.image_url}`
                  }
                  alt={displayTitle}
                  className="w-full max-h-72 object-cover rounded-xl border border-slate-200/80 shadow-2xs"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = "none";
                  }}
                />
              </div>
            )}

            {/* Rich Text Body */}
            <div className="p-4 sm:p-6 sm:py-7">
              {state.description ? (
                <div
                  className="prose max-w-none text-slate-700 text-sm leading-relaxed space-y-4
                    [&>h1]:text-2xl [&>h1]:font-bold [&>h1]:text-slate-900 [&>h1]:mb-3
                    [&>h2]:text-xl [&>h2]:font-bold [&>h2]:text-slate-900 [&>h2]:mt-6 [&>h2]:mb-2
                    [&>h3]:text-base [&>h3]:font-bold [&>h3]:text-slate-800 [&>h3]:mt-4 [&>h3]:mb-1.5
                    [&>p]:mb-3 [&>p]:leading-relaxed
                    [&>ul]:list-disc [&>ul]:pl-5 [&>ul]:space-y-1 [&>ul]:mb-3
                    [&>ol]:list-decimal [&>ol]:pl-5 [&>ol]:space-y-1 [&>ol]:mb-3
                    [&>li]:text-slate-700
                    [&>a]:text-[#006747] [&>a]:underline [&>a]:font-medium hover:[&>a]:text-[#00553a]
                    [&>blockquote]:border-l-4 [&>blockquote]:border-[#006747] [&>blockquote]:pl-4 [&>blockquote]:italic [&>blockquote]:text-slate-600
                    [&>table]:w-full [&>table]:border-collapse [&>table]:border [&>table]:border-slate-200
                    [&>table_th]:bg-slate-50 [&>table_th]:p-2.5 [&>table_th]:border [&>table_th]:border-slate-200 [&>table_th]:text-left
                    [&>table_td]:p-2.5 [&>table_td]:border [&>table_td]:border-slate-200"
                  dangerouslySetInnerHTML={{ __html: state.description }}
                />
              ) : (
                <div className="py-12 text-center text-slate-400">
                  <FileText className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                  <p className="text-sm font-medium text-slate-600">No content available for this policy</p>
                  <p className="text-xs text-slate-400 mt-1">Click "Edit Content" above to write or paste text</p>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PoliciesDetailsPage;
